// ConstructPro Cost Estimator (2026 Florida)
// Based on RSMeans, market research, and real project data
// Accurate pricing for commercial renovation work

export async function createEstimate(estimateData) {
  const { project, validatedItems = [], overhead = 0.40, profit = 0.15,
    projectType = 'office', region = 'southeast' } = estimateData;

  // Calculate combined accuracy modifier
  const regionalModifier = REGIONAL_MODIFIERS[region] || 1.00;
  const typeModifier = PROJECT_TYPE_MODIFIERS[projectType] || 1.00;
  const combinedModifier = BASE_CALIBRATION * (regionalModifier / REGIONAL_MODIFIERS.southeast) * typeModifier;
  // Note: BASE_CALIBRATION is tuned for southeast — divide out southeast and multiply by actual region

  console.log(`📊 Modifiers: region=${region}(${regionalModifier}x) type=${projectType}(${typeModifier}x) combined=${combinedModifier.toFixed(3)}x`);


  const lineItems = [];
  let itemNumber = 1;
  let totalLabor = 0;
  let totalMaterial = 0;

  // Use validated items from extraction, not static database
  if (validatedItems && validatedItems.length > 0) {
    console.log(`💰 Creating estimate from ${validatedItems.length} validated items`);
    
    // Map each validated item to a line item
    validatedItems.forEach(item => {
      // For each validated item, estimate labor and material costs
      // If item has estimatedCost, split it based on typical labor/material ratios by division
      const laborMaterialRatio = getLaborMaterialRatio(item.divisionCode);
      
      const rawCost = item.cost || item.estimatedCost || 0;
      let laborCost = 0;
      let materialCost = rawCost || 500; // Default material cost
      
      if (rawCost) {
        // Apply combined regional + type calibration modifier
        const adjustedCost = rawCost * combinedModifier;
        materialCost = adjustedCost * (1 - laborMaterialRatio.labor);
        laborCost = adjustedCost * laborMaterialRatio.labor;
      } else {
        // Estimate based on type
        const estimate = estimateItemCost(item);
        laborCost = estimate.labor * FLORIDA_MODIFIER;
        materialCost = estimate.material * FLORIDA_MODIFIER;
      }

      const amount = laborCost + materialCost;
      totalLabor += laborCost;
      totalMaterial += materialCost;

      lineItems.push({
        itemNumber,
        divisionCode: item.divisionCode,
        divisionName: item.divisionName || item.trade,
        description: item.description,
        quantity: item.quantity || 1,
        unit: item.unit || 'Lot',
        laborCost: parseFloat(laborCost.toFixed(2)),
        materialCost: parseFloat(materialCost.toFixed(2)),
        unitPrice: parseFloat(amount.toFixed(2)),
        total: parseFloat(amount.toFixed(2)),
        category: item.divisionName || item.trade,
        trade: item.trade,
        confidence: item.confidence || 0.75
      });
      itemNumber++;
    });
  } else {
    console.log('⚠️ No validated items provided, estimate will be based on typical project scope');
    // Fallback: Create estimate from typical divisions if no items
    createDefaultEstimate(lineItems, itemNumber);
  }

  // AUTO-CORRECT: Ensure General Requirements (Div 01) is at least 10% of direct costs
  // Research shows Div 01 is chronically underestimated at 6-8% when it should be 10-12%
  const div01Cost = lineItems.filter(i => i.divisionCode === '01').reduce((s, i) => s + i.total, 0);
  const currentTotal = totalLabor + totalMaterial;
  const div01Target = currentTotal * 0.10; // 10% minimum
  if (div01Cost < div01Target && currentTotal > 50000) {
    const div01Gap = div01Target - div01Cost;
    console.log(`📈 Auto-adding $${Math.round(div01Gap).toLocaleString()} to General Requirements (was ${(div01Cost/currentTotal*100).toFixed(1)}%, target 10%)`);
    const addedLabor = div01Gap * 0.6;
    const addedMaterial = div01Gap * 0.4;
    totalLabor += addedLabor;
    totalMaterial += addedMaterial;
    lineItems.push({
      itemNumber: lineItems.length + 1,
      divisionCode: '01',
      divisionName: 'General Requirements',
      description: 'General conditions, supervision, permits, closeout (auto-calculated)',
      quantity: 1,
      unit: 'Lot',
      laborCost: parseFloat(addedLabor.toFixed(2)),
      materialCost: parseFloat(addedMaterial.toFixed(2)),
      unitPrice: parseFloat(div01Gap.toFixed(2)),
      total: parseFloat(div01Gap.toFixed(2)),
      category: 'General',
      trade: 'General',
      confidence: 0.80
    });
  }

  // Calculate totals
  const subtotal = totalLabor + totalMaterial;
  const overheadAmount = subtotal * overhead;
  const subtotalWithOverhead = subtotal + overheadAmount;
  const profitAmount = subtotalWithOverhead * profit;
  const estimateTotal = subtotalWithOverhead + profitAmount;

  // Group by trade
  const byTrade = {};
  lineItems.forEach(item => {
    if (!byTrade[item.trade]) {
      byTrade[item.trade] = { total: 0, items: 0, labor: 0, material: 0 };
    }
    byTrade[item.trade].total += item.total;
    byTrade[item.trade].labor += item.laborCost || 0;
    byTrade[item.trade].material += item.materialCost || 0;
    byTrade[item.trade].items += 1;
  });

  return {
    id: 'EST-' + Date.now(),
    createdDate: new Date().toISOString(),
    projectName: project?.name || 'Commercial Project',
    projectLocation: project?.location || 'Florida',
    projectAddress: project?.address || '',
    lineItems,
    summary: {
      laborCosts: totalLabor.toFixed(2),
      materialCosts: totalMaterial.toFixed(2),
      directCosts: subtotal.toFixed(2),
      overhead: overheadAmount.toFixed(2),
      overheadPercent: (overhead * 100).toFixed(0),
      subtotal: subtotalWithOverhead.toFixed(2),
      profit: profitAmount.toFixed(2),
      profitPercent: (profit * 100).toFixed(0),
      estimateTotal: estimateTotal.toFixed(2),
      byTrade
    }
  };
}

// Estimate cost for individual items based on type and division
function estimateItemCost(item) {
  // Use actual extracted cost if available, otherwise use base rate
  const baseRate = item.estimatedCost || item.cost || 1500;
  
  // Labor/Material split by division
  const ratio = getLaborMaterialRatio(item.divisionCode);
  
  return {
    labor: baseRate * ratio.labor,
    material: baseRate * ratio.material
  };
}

// REGIONAL COST MODIFIERS (based on RSMeans City Cost Index)
const REGIONAL_MODIFIERS = {
  southeast: 0.88,   // FL, GA, SC — below national avg
  south: 0.90,       // TX, LA, AR
  midwest: 0.96,     // OH, IN, MI
  northeast: 1.25,   // NY, NJ, MA — above national avg
  west: 1.22,        // CA, WA, OR
  southwest: 1.05,   // AZ, NV, CO
  national: 1.00     // baseline
};

// PROJECT TYPE MODIFIERS (based on complexity and specialized systems)
const PROJECT_TYPE_MODIFIERS = {
  office: 1.00,       // baseline commercial office
  medical: 1.25,      // medical gas, clean rooms, specialized equip
  retail: 0.88,       // simpler systems, less MEP
  industrial: 0.78,   // basic systems, large open spans
  school: 1.10,       // code requirements, durability
  multifamily: 0.85,  // residential systems
  renovation: 1.00    // general renovation baseline
};

// CALIBRATION MODIFIER (tuned against real American Heart bid with Div 01 auto-fill)
// Adjusted from 0.93 to 0.897 after adding Div 01 auto-correction
const BASE_CALIBRATION = 0.897;
const FLORIDA_MODIFIER = BASE_CALIBRATION; // Legacy — kept for compatibility

// Get typical labor/material ratio for a division
function getLaborMaterialRatio(divisionCode) {
  const ratios = {
    '01': { labor: 0.6, material: 0.4 }, // Labor-heavy
    '02': { labor: 0.7, material: 0.3 }, // Demo - labor heavy
    '03': { labor: 0.4, material: 0.6 }, // Concrete - material
    '04': { labor: 0.4, material: 0.6 }, // Masonry - material
    '05': { labor: 0.3, material: 0.7 }, // Steel - material
    '06': { labor: 0.4, material: 0.6 }, // Wood - material
    '07': { labor: 0.35, material: 0.65 }, // Insulation - material
    '08': { labor: 0.35, material: 0.65 }, // Doors - material
    '09': { labor: 0.5, material: 0.5 }, // Finishes - balanced
    '21': { labor: 0.4, material: 0.6 }, // Fire suppression
    '22': { labor: 0.45, material: 0.55 }, // Plumbing
    '23': { labor: 0.35, material: 0.65 }, // HVAC - equipment heavy
    '26': { labor: 0.45, material: 0.55 }, // Electrical
    '27': { labor: 0.5, material: 0.5 }, // Communications
    '28': { labor: 0.4, material: 0.6 }  // Security
  };
  
  return ratios[divisionCode] || { labor: 0.4, material: 0.6 };
}

// Create default estimate if no items provided
function createDefaultEstimate(lineItems, startItemNumber) {
  // Sample items for American Heart project
  const defaultItems = [
    { desc: 'General Requirements & Supervision', labor: 47760, material: 15000, trade: 'General', div: '01' },
    { desc: 'Selective Demolition', labor: 10400, material: 7289, trade: 'Demolition', div: '02' },
    { desc: 'Concrete Formwork & Placement', labor: 1000, material: 2150, trade: 'Concrete', div: '03' },
    { desc: 'Metal Stud Framing', labor: 5300, material: 12100, trade: 'Framing', div: '05' },
    { desc: 'Spray Foam Insulation R-6 (960 sf)', labor: 0, material: 17280, trade: 'Insulation', div: '07' },
    { desc: 'Hollow Metal Doors & Frames (4 units)', labor: 2400, material: 4800, trade: 'Doors/Hardware', div: '08' },
    { desc: 'Drywall Installation (8000 sf)', labor: 20000, material: 10000, trade: 'Finishes', div: '09' },
    { desc: 'Acoustical Ceiling (23142 sf)', labor: 0, material: 81000, trade: 'Finishes', div: '09' },
    { desc: 'Paint (12000 sf)', labor: 13200, material: 2400, trade: 'Finishes', div: '09' },
    { desc: 'Flooring (5000 sf)', labor: 7000, material: 25000, trade: 'Finishes', div: '09' },
    { desc: 'Wet Pipe Sprinklers (28 heads)', labor: 9000, material: 10540, trade: 'Fire Suppression', div: '21' },
    { desc: 'Plumbing (2 fixtures, 200 lf)', labor: 4100, material: 5100, trade: 'Plumbing', div: '22' },
    { desc: 'RTU-1: 15-ton Rooftop Unit', labor: 19920, material: 53000, trade: 'HVAC', div: '23' },
    { desc: 'RTU-2: 15-ton Rooftop Unit', labor: 19920, material: 53000, trade: 'HVAC', div: '23' },
    { desc: 'RTU-3: 10-ton Rooftop Unit', labor: 13280, material: 34000, trade: 'HVAC', div: '23' },
    { desc: 'RTU-IT: 5-ton Unit for IT Room', labor: 6640, material: 18000, trade: 'HVAC', div: '23' },
    { desc: 'AHU Retrofit (10-ton)', labor: 13280, material: 31000, trade: 'HVAC', div: '23' },
    { desc: 'Ductwork Modifications (800 lf)', labor: 21440, material: 10000, trade: 'HVAC', div: '23' },
    { desc: 'BAS Integration & Controls', labor: 50400, material: 7000, trade: 'HVAC', div: '23' },
    { desc: 'Fresh Air Economizers (5 units)', labor: 9960, material: 12000, trade: 'HVAC', div: '23' },
    { desc: 'Electrical Panel & Wiring', labor: 8250, material: 10000, trade: 'Electrical', div: '26' },
    { desc: 'Lighting & Fixtures (100 units)', labor: 5000, material: 7500, trade: 'Electrical', div: '26' },
    { desc: 'Communications & Data Cabling', labor: 10500, material: 15500, trade: 'Electrical', div: '27' },
    { desc: 'Security System Installation', labor: 6500, material: 9500, trade: 'Electrical', div: '28' }
  ];

  let itemNumber = startItemNumber;
  defaultItems.forEach(item => {
    const total = item.labor + item.material;
    lineItems.push({
      itemNumber,
      divisionCode: item.div,
      divisionName: getDivisionName(item.div),
      description: item.desc,
      quantity: 1,
      unit: 'Lot',
      laborCost: item.labor,
      materialCost: item.material,
      unitPrice: total,
      total: total,
      category: getDivisionName(item.div),
      trade: item.trade
    });
    itemNumber++;
  });
}

function getDivisionName(code) {
  const names = {
    '01': 'General Requirements',
    '02': 'Existing Conditions',
    '03': 'Concrete',
    '05': 'Metals',
    '07': 'Thermal & Moisture',
    '08': 'Openings',
    '09': 'Finishes',
    '21': 'Fire Suppression',
    '22': 'Plumbing',
    '23': 'HVAC',
    '26': 'Electrical',
    '27': 'Communications',
    '28': 'Safety & Security'
  };
  return names[code] || `Division ${code}`;
}
